import { capitalize } from "lodash";

import {
  PATIENT_PRODUCT_STATUSES,
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_ON_LOAN
} from "constants/patient-products";
import {
  ACCESSORIES_SUB_TYPES,
  BATTERY_COLORS,
  BATTERY_SUBTYPE,
  CHARGER_SUBTYPE,
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  HEARING_AID_TYPES,
  OTHER_SUBTYPE,
  RECEIVER_SUBTYPE,
  REMOTE_SUBTYPE,
  TECHNOLOGIES,
  TUBE_SUBTYPE
} from "constants/products";
import { joinValues } from "features/patients/sections/patient-hearing-aids/new-hearing-aid/new-hearing-aid-wizard/utils/new-hearing-aid-wizard.utils";
import { AssignedTag } from "services/types/assigned-tag.type";
import {
  type BatteryInventoryProductSpecification,
  type ChargerInventoryProductSpecification,
  type DomeInventoryProductSpecification,
  type EarmoldInventoryProductSpecification,
  type HearingAidInventoryProductSpecification,
  type InventoryProduct,
  type OtherInventoryProductSpecification,
  type ReceiverInventoryProductSpecification,
  type RemoteInventoryProductSpecification,
  type TubeInventoryProductSpecification
} from "services/types/inventory-products.type";
import {
  type BatterySpecification,
  type HearingAidMasterProductSpecification
} from "services/types/master-products.type";
import {
  type PatientProduct,
  type PatientProductFilters,
  type PatientProductType
} from "services/types/patient-products.type";

import {
  INVENTORY_PRODUCT_STATUS_ON_DEMO,
  INVENTORY_PRODUCT_STATUS_ON_LOAN
} from "../constants/inventory-products";
import {
  ACCESSORY_PRODUCT_TYPE,
  COCHLEAR_IMPLANT_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SUPPLY_PRODUCT_TYPE
} from "../constants/sales";
import {
  formatBackendDateOnly,
  formatValidQueryParamUrlCommaSeparated
} from "./common";

export type ActivePatientProduct = {
  id: number;
  condition?: number | null;
  sale_id?: number | null;
  sale_item?: number | null;
  assigned_tags?: AssignedTag[];
  inventory_product_id: number | null;
  inventory_product?: InventoryProduct;
  product_type: PatientProductType;
  display_name: string;
  ear: "L" | "R";
  serial_number?: string;
  order_number?: string;
  status: string;
  status_code: number;
  statuses: { created_at: string; value: number }[];
  inventory_product_status: number | null;
  purchase_date: string;
  service_plan_expiration_date?: string;
  manufacturer_warranty?: string;
  lost_and_damage_warranty?: string;
  extended_warranty_expiration_date?: string;
  due_date?: string;
  manufacturer?: string;
  model?: string;
  technology?: string;
  type?: string;
  gain?: string;
  description?: string;
  dome_type?: string;
  dome_size?: string;
  tube_size?: string;
  length?: number;
  name?: string;
  color?: string;
  battery?: string;
  manufacturer_instruction?: string;
  canal_length?: string;
  venting?: string;
  material?: string;
  options?: string;
  additional_notes?: string | null;
  quantity?: number;
  is_in_use?: boolean;
  loanable?: boolean;
  demoable?: boolean;
  delivered_at: string;
  delivered_at_display?: string | null;
};

export const isProductOfType =
  (productType: PatientProductType) => (patientProduct: PatientProduct) => {
    const { type, subtype } =
      patientProduct?.inventory_product?.product?.master_product ||
      patientProduct ||
      {};
    switch (productType) {
      case "hearing-aid":
        return type === 2 || type === undefined;
      case "cochlear-implant":
        return type === 6;
      case "receiver":
        return type === 3 && subtype === RECEIVER_SUBTYPE;
      case "earmold":
        return type === 3 && subtype === EAR_MOLD_SUBTYPE;
      case "dome":
        return type === 3 && subtype === DOME_SUBTYPE;
      case "tube":
        return type === 3 && subtype === TUBE_SUBTYPE;
      case "accessories":
        return (
          type === 3 &&
          [
            BATTERY_SUBTYPE,
            CHARGER_SUBTYPE,
            REMOTE_SUBTYPE,
            OTHER_SUBTYPE
          ].includes(Number(subtype))
        );

      default:
        return false;
    }
  };

export const getProductTypes = (
  productType: PatientProductType | undefined
): {
  type: number | null;
  subtype: [number, number, number, number] | [number] | [null];
} => {
  switch (productType) {
    case "hearing-aid":
      return { type: 2, subtype: [null] };
    case "receiver":
      return { type: 3, subtype: [RECEIVER_SUBTYPE] };
    case "tube":
      return { type: 3, subtype: [TUBE_SUBTYPE] };
    case "dome":
      return { type: 3, subtype: [DOME_SUBTYPE] };
    case "earmold":
      return { type: 3, subtype: [EAR_MOLD_SUBTYPE] };
    case "battery":
      return { type: 3, subtype: [BATTERY_SUBTYPE] };
    case "charger":
      return { type: 3, subtype: [CHARGER_SUBTYPE] };
    case "remote":
      return { type: 3, subtype: [REMOTE_SUBTYPE] };
    case "other":
      return { type: 3, subtype: [OTHER_SUBTYPE] };
    case "accessories":
      return {
        type: 3,
        subtype: [
          BATTERY_SUBTYPE,
          CHARGER_SUBTYPE,
          REMOTE_SUBTYPE,
          OTHER_SUBTYPE
        ]
      };
    case "cochlear-implant": {
      return { type: 6, subtype: [null] };
    }
    default:
      return { type: null, subtype: [null] };
  }
};

export function getActiveHearingAid(
  hearingAidsProducts: PatientProduct[],
  ear: "L" | "R" | ""
) {
  const earHearingAids = hearingAidsProducts.filter(
    (patientProduct) => patientProduct.ear === ear
  );
  return earHearingAids.map((ha) => convertToDisplay(ha));
}

export function convertToDisplay(
  patientProduct: PatientProduct
): Partial<ActivePatientProduct> {
  if (!patientProduct) {
    return {};
  }

  const manufacturer_warranty_expiration_date =
    patientProduct?.manufacturer_warranty_expiration_date ??
    patientProduct?.inventory_product?.manufacturer_warranty_expiration_date;

  const patientProductManufacturerWarranty =
    manufacturer_warranty_expiration_date
      ? formatBackendDateOnly(
          manufacturer_warranty_expiration_date,
          "MMM dd, yyyy"
        )
      : "";

  const service_plan_expiration_date =
    patientProduct?.service_plan_expiration_date
      ? formatBackendDateOnly(
          patientProduct?.service_plan_expiration_date,
          "MMM dd, yyyy"
        )
      : "";

  const loss_damage_warranty_expiration_date =
    patientProduct?.loss_damage_warranty_expiration_date ??
    patientProduct?.inventory_product?.loss_damage_warranty_expiration_date;

  const patientProductLostAndDamageWarranty =
    loss_damage_warranty_expiration_date
      ? formatBackendDateOnly(
          loss_damage_warranty_expiration_date,
          "MMM dd, yyyy"
        )
      : "";

  const extended_warranty_expiration_date =
    patientProduct?.extended_warranty_expiration_date;

  const patientProductExtendedWarrantyExpirationDate =
    extended_warranty_expiration_date
      ? formatBackendDateOnly(extended_warranty_expiration_date, "MMM dd, yyyy")
      : "";

  const patientProductPurchaseDate = patientProduct.purchase_date
    ? formatBackendDateOnly(patientProduct.purchase_date, "MMM dd, yyyy")
    : "";

  const patientProductDeliveredAt = patientProduct.delivered_at
    ? formatBackendDateOnly(patientProduct.delivered_at, "MMM dd, yyyy")
    : "";

  const patientProductDueDate = patientProduct.due_date
    ? formatBackendDateOnly(patientProduct.due_date, "MMM dd, yyyy")
    : "";
  const patientProductSerialNumber = patientProduct.serial_number || "";

  if (!patientProduct.inventory_product) {
    return {
      id: patientProduct.id,
      sale_id: patientProduct.sale?.id,
      sale_item: patientProduct.sale_item,
      status_code: patientProduct.status,
      statuses: patientProduct.statuses,
      assigned_tags: patientProduct.assigned_tags,
      status: PATIENT_PRODUCT_STATUSES.getFromCode(patientProduct.status),
      inventory_product_id: null,
      inventory_product_status: null,
      ear: patientProduct.ear,
      serial_number: patientProductSerialNumber,
      description: patientProduct.description,
      display_name: patientProduct.description,
      service_plan_expiration_date,
      manufacturer_warranty: patientProductManufacturerWarranty,
      lost_and_damage_warranty: patientProductLostAndDamageWarranty,
      extended_warranty_expiration_date:
        patientProductExtendedWarrantyExpirationDate,
      purchase_date: patientProductPurchaseDate,
      due_date: patientProductDueDate,
      product_type: getProductType({
        type: patientProduct.type,
        subtype: patientProduct.subtype
      }),
      is_in_use: patientProduct.is_in_use,
      loanable: false,
      demoable: false,
      delivered_at_display: patientProduct.delivered_at_display,
      delivered_at: patientProductDeliveredAt,
      additional_notes: patientProduct.notes
    };
  }
  const { product, serial_number } = patientProduct.inventory_product;
  const { master_product, display_name } = product!;

  const effectiveSubtype =
    master_product.type === 2 || master_product.type === 6
      ? null
      : master_product.subtype;
  switch (effectiveSubtype?.toString() || "null") {
    case "null": {
      const { ear, order_item, status, inventory_product, sale } =
        patientProduct;
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};

      const purchase_date = sale?.service_date || patientProduct.purchase_date;
      const { tracking_number: order_number } = order_item || {};
      const { model, specification } = master_product;
      const manufacturer = master_product.manufacturer?.name ?? "";
      const { technology, type } =
        (specification as HearingAidMasterProductSpecification) || {};
      const { color, battery, manufacturer_instruction, additional_notes } =
        (inventory_product?.specification as HearingAidInventoryProductSpecification) ||
        {};
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        assigned_tags: patientProduct.assigned_tags,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        product_type: "hearing-aid",
        display_name: display_name || "",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        manufacturer,
        model,
        technology: TECHNOLOGIES.getFromCode(technology),
        type: HEARING_AID_TYPES.getFromCode(type),
        manufacturer_instruction,
        color,
        battery,
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "1": {
      const specification = patientProduct.inventory_product
        .specification as ReceiverInventoryProductSpecification;
      const { ear, order_item, status, inventory_product, sale } =
        patientProduct;
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const { model } = master_product;
      const manufacturer = master_product.manufacturer?.name ?? "";
      const { tracking_number: order_number } = order_item || {};
      const { gain, length, additional_notes } = specification || {};
      return {
        product_type: "receiver",
        display_name: display_name || "",
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        assigned_tags: patientProduct.assigned_tags,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        manufacturer,
        gain: gain?.toString(),
        length,
        model,
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "2": {
      const { master_product } = patientProduct.inventory_product.product!;
      const { ear, order_item, status, inventory_product, sale } =
        patientProduct;
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product;
      const specification =
        inventory_product.specification as EarmoldInventoryProductSpecification;
      const { canal_length, venting, material, options, additional_notes } =
        specification || {};
      const purchase_date = sale?.service_date;
      const { tracking_number: order_number } = order_item || {};
      const { description: type } = master_product;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        assigned_tags: patientProduct.assigned_tags,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        display_name: display_name || "",
        product_type: "earmold",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        type,
        canal_length,
        venting: venting || "",
        material: material || "",
        options: options || "",
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "3": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const { tracking_number: order_number } = order_item || {};
      const tubeSpecification =
        (inventory_product.specification as TubeInventoryProductSpecification) ||
        {};
      const { additional_notes, size: tube_size } = tubeSpecification;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        assigned_tags: patientProduct.assigned_tags,
        sale_item: patientProduct.sale_item,
        product_type: "tube",
        display_name: display_name || "",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        tube_size: tube_size || "",
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "4": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const domeSpecification =
        (inventory_product.specification as DomeInventoryProductSpecification) ||
        {};
      const {
        additional_notes,
        size: dome_size,
        type: dome_type
      } = domeSpecification;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        product_type: "dome",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        dome_size: dome_size || "",
        dome_type: dome_type || "",
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "5": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const { specification } = master_product;
      const { color } = (specification as BatterySpecification) || {};
      const spec =
        (inventory_product.specification as BatteryInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "battery",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        color: BATTERY_COLORS.getFromCode(color),
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "6": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;

      const spec =
        (inventory_product.specification as ChargerInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        assigned_tags: patientProduct.assigned_tags,
        sale_item: patientProduct.sale_item,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "charger",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "9": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;

      const spec =
        (inventory_product.specification as RemoteInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "remote",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "999": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;

      const spec =
        (inventory_product.specification as OtherInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: sale?.id,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "other",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    default:
      return {};
  }
}

export const getQuantity = (sale: any, inventoryProductId: number) => {
  return (
    sale?.items?.find((item: any) => item?.product?.id === inventoryProductId)
      ?.quantity || 0
  );
};

// A patient-product can only be re-activated when the device is physically
// with the patient (DELIVERED or ON_LOAN). Activating in any other state
// triggers stock-routing bugs on the next return cycle (see DEV-1711).
const PATIENT_PRODUCT_STATUSES_ALLOWING_ACTIVATION: number[] = [
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_ON_LOAN
];

export const canActivatePatientProduct = (status: number | undefined | null) =>
  status != null &&
  PATIENT_PRODUCT_STATUSES_ALLOWING_ACTIVATION.includes(status);

export const PATIENT_PRODUCT_ACTIVATION_BLOCKED_TOOLTIP =
  "This device is not currently with the patient and can't be reactivated here. To re-issue it, create a new sale or loan.";

export const isProductConditional = ({
  status,
  inventoryProductStatus,
  isPatientProduct = false,
  isLoanable = false,
  isDemoable = false
}: {
  status?: string | number;
  inventoryProductStatus?: number | null;
  isPatientProduct?: boolean;
  isLoanable?: boolean;
  isDemoable?: boolean;
}) => {
  if (isPatientProduct) {
    let patientProductCondition = false;
    if (typeof status === "string") {
      patientProductCondition = [
        PATIENT_PRODUCT_STATUSES.getFromCode(PATIENT_PRODUCT_STATUS_ON_LOAN)
      ].includes(status);
    } else if (typeof status === "number") {
      patientProductCondition = [PATIENT_PRODUCT_STATUS_ON_LOAN].includes(
        status
      );
    }
    return (
      patientProductCondition ||
      [
        INVENTORY_PRODUCT_STATUS_ON_LOAN,
        INVENTORY_PRODUCT_STATUS_ON_DEMO
      ].includes(inventoryProductStatus || -1)
    );
  } else {
    return isLoanable || isDemoable;
  }
};

export function getCurrentHearingAid(
  hearingAid: Omit<PatientProduct, "patient"> | null | undefined
): string {
  if (!hearingAid) return "No active hearing aid";
  const { inventory_product, description } = hearingAid;
  if (inventory_product === null || inventory_product === undefined) {
    return description;
  }
  const { product } = inventory_product;
  return joinValues([product?.display_name]);
}

export function getCurrentHearingAids(
  leftHearingAid: Omit<PatientProduct, "patient"> | null | undefined,
  rightHearingAid: Omit<PatientProduct, "patient"> | null | undefined
): string {
  if (!leftHearingAid && !rightHearingAid) {
    return "No active hearing aids.";
  }
  const activeHearingAids: string[] = [];
  if (leftHearingAid) {
    activeHearingAids.push("L: " + getCurrentHearingAid(leftHearingAid));
  }
  if (rightHearingAid) {
    activeHearingAids.push("R: " + getCurrentHearingAid(rightHearingAid));
  }
  return activeHearingAids.join(", ");
}

export function getProductType({
  type,
  subtype
}: {
  type: number | null | undefined;
  subtype: number | null | undefined;
}): PatientProductType | undefined {
  if (type?.toString() === ACCESSORY_PRODUCT_TYPE.toString()) {
    switch (subtype?.toString()) {
      case RECEIVER_SUBTYPE.toString():
        return "receiver";
      case EAR_MOLD_SUBTYPE.toString():
        return "earmold";
      case DOME_SUBTYPE.toString():
        return "dome";
      case TUBE_SUBTYPE.toString():
        return "tube";
      case BATTERY_SUBTYPE.toString():
        return "battery";
      case CHARGER_SUBTYPE.toString():
        return "charger";
      case REMOTE_SUBTYPE.toString():
        return "remote";
      case OTHER_SUBTYPE.toString():
        return "other";
      default:
        return "other";
    }
  } else if (type?.toString() === HEARING_AID_PRODUCT_TYPE.toString()) {
    return "hearing-aid";
  } else if (type?.toString() === SUPPLY_PRODUCT_TYPE.toString()) {
    return "supply";
  } else if (type?.toString() === COCHLEAR_IMPLANT_PRODUCT_TYPE.toString()) {
    return "cochlear-implant";
  } else {
    return undefined;
  }
}

export function productTypeName(productType: string) {
  if (!productType) return "";
  if (productType === "hearing-aid") return "Hearing Aid";
  if (productType === "cochlear-implant") return "Cochlear Implant";

  return capitalize(productType);
}

export function getProductDisplayType({
  type,
  subtype
}: {
  type?: number | string | null;
  subtype?: number | string | null;
}) {
  return type?.toString() === ACCESSORY_PRODUCT_TYPE.toString()
    ? subtype
      ? ACCESSORIES_SUB_TYPES.getFromCode(subtype)
      : PRODUCT_TYPES.getFromCode(type)
    : [
          HEARING_AID_PRODUCT_TYPE.toString(),
          SUPPLY_PRODUCT_TYPE.toString()
        ].includes(type?.toString() || "")
      ? PRODUCT_TYPES.getFromCode(type)
      : "N/A";
}

export const formatPatientProductFilters = ({
  patientId,
  query,
  baseFilters,
  ...filters
}: PatientProductFilters) => {
  return {
    ...baseFilters,
    ...filters,
    patient: patientId
  };
};

export const getPatientProductsEndpointWithQueryParameters = (
  baseUrl: string,
  { query = "{*}", search, ...filters }: PatientProductFilters
) => {
  return `${baseUrl}?${formatValidQueryParamUrlCommaSeparated(
    formatPatientProductFilters({
      ...filters,
      search: search && encodeURIComponent(search)
    })
  )}&query=${query}`;
};
